import React, { useState, useMemo } from 'react';
import api from '../utils/api';
import { 
  FileText, Download, CheckCircle, Clock, AlertTriangle, 
  DollarSign, TrendingDown, Phone, Calendar, Calculator,
  Target, ChevronDown, ChevronUp, Printer, Copy, Check,
  Banknote, Home, Shield, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { calculatePayment, calculateDaysToRenewal } from '../utils/calculations';
import { trackFeatureUsed } from '../utils/analytics';



const ActionPlanGenerator = ({ mortgage, marketRates, user, shockData }) => {
  const [showFullPlan, setShowFullPlan] = useState(false);
  const [copiedSection, setCopiedSection] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  // Generate comprehensive action plan based on shock data
  const actionPlan = useMemo(() => {
    if (!shockData || !mortgage) return null;
    
    const daysToRenewal = shockData.daysToRenewal;
    const severity = shockData.severity;
    const monthlyIncrease = shockData.monthlyIncrease;
    const annualIncrease = shockData.annualIncrease;
    const currentRate = shockData.currentRate;
    const renewalRate = shockData.renewalRate;
    
    // Determine urgency phase
    let urgencyPhase = 'planning';
    if (daysToRenewal <= 30) urgencyPhase = 'critical';
    else if (daysToRenewal <= 60) urgencyPhase = 'urgent';
    else if (daysToRenewal <= 90) urgencyPhase = 'active';
    
    // Calculate key metrics (rounded for clean display)
    const monthlyBudgetAdjustment = Math.ceil(monthlyIncrease / 30) * 30; // Round up to nearest $30
    const savingsNeeded = Math.round(monthlyIncrease * 3); // 3-month emergency buffer
    const weeksToRenewal = Math.floor(daysToRenewal / 7);
    const weeklyBudgetAdjustment = Math.round(Math.ceil(monthlyIncrease / 4.33));
    
    // Generate timeline with specific dates
    const today = new Date();
    const renewalDate = new Date(mortgage.renewal_date);
    
    const milestones = [];
    
    // Week 1-2: Research & Compare
    const week2Date = new Date(today);
    week2Date.setDate(week2Date.getDate() + 14);
    milestones.push({
      phase: 'Research',
      deadline: week2Date.toLocaleDateString('en-CA'),
      daysFromNow: Math.min(14, daysToRenewal),
      tasks: [
        'Get rate quotes from at least 3 lenders',
        'Check current lender\'s retention rates',
        'Review your credit score and report',
        'Gather all mortgage documents'
      ],
      priority: 'high'
    });
    
    // Week 3-4: Negotiate & Compare
    if (daysToRenewal > 14) {
      const week4Date = new Date(today);
      week4Date.setDate(week4Date.getDate() + 28);
      milestones.push({
        phase: 'Negotiate',
        deadline: week4Date.toLocaleDateString('en-CA'),
        daysFromNow: Math.min(28, daysToRenewal),
        tasks: [
          'Call current lender to negotiate better rate',
          'Get written rate offers from competitors',
          'Calculate total costs of switching vs staying',
          'Review early payout penalties if switching'
        ],
        priority: daysToRenewal <= 60 ? 'high' : 'medium'
      });
    }
    
    // 30 days before: Make Decision
    if (daysToRenewal > 30) {
      const decision30 = new Date(renewalDate);
      decision30.setDate(decision30.getDate() - 30);
      milestones.push({
        phase: 'Decision',
        deadline: decision30.toLocaleDateString('en-CA'),
        daysFromNow: daysToRenewal - 30,
        tasks: [
          'Make final decision: renew or switch',
          'If switching, begin application process',
          'Set up new pre-authorized payments',
          'Confirm all terms in writing'
        ],
        priority: 'high'
      });
    }
    
    // 14 days before: Final Prep
    if (daysToRenewal > 14) {
      const prep14 = new Date(renewalDate);
      prep14.setDate(prep14.getDate() - 14);
      milestones.push({
        phase: 'Final Prep',
        deadline: prep14.toLocaleDateString('en-CA'),
        daysFromNow: daysToRenewal - 14,
        tasks: [
          'Confirm new payment amount and date',
          'Update budget spreadsheet with new payment',
          'Ensure sufficient funds for first new payment',
          'Set up rate alerts for future monitoring'
        ],
        priority: 'medium'
      });
    }
    
    // Budget adjustment strategies
    const budgetStrategies = [];
    
    if (monthlyIncrease > 0) {
      // Calculate specific cuts needed (rounded to whole dollars)
      const subscriptionsCut = Math.round(Math.min(monthlyIncrease * 0.2, 100));
      const diningCut = Math.round(Math.min(monthlyIncrease * 0.3, 200));
      const entertainmentCut = Math.round(Math.min(monthlyIncrease * 0.2, 150));
      const grocerySavings = Math.round(Math.min(monthlyIncrease * 0.15, 100));
      const energySavings = Math.round(Math.min(monthlyIncrease * 0.15, 75));
      
      budgetStrategies.push({
        category: 'Subscriptions',
        monthlySavings: subscriptionsCut,
        actions: [
          'Audit all streaming services - cancel unused',
          'Review gym memberships and subscriptions',
          'Check for duplicate services',
          'Negotiate better rates on remaining services'
        ],
        icon: 'tv'
      });
      
      budgetStrategies.push({
        category: 'Dining & Takeout',
        monthlySavings: diningCut,
        actions: [
          'Limit restaurant visits to special occasions',
          'Meal prep on weekends to avoid takeout',
          'Use cashback apps for grocery shopping',
          'Pack lunches instead of buying'
        ],
        icon: 'utensils'
      });
      
      budgetStrategies.push({
        category: 'Entertainment',
        monthlySavings: entertainmentCut,
        actions: [
          'Use library for books, movies, and games',
          'Find free local events and activities',
          'Host potluck dinners instead of going out',
          'Use credit card rewards for entertainment'
        ],
        icon: 'ticket'
      });
      
      budgetStrategies.push({
        category: 'Groceries',
        monthlySavings: grocerySavings,
        actions: [
          'Plan meals around weekly flyer sales',
          'Buy store brands instead of name brands',
          'Use apps like Flipp and Checkout 51',
          'Buy in bulk for non-perishables'
        ],
        icon: 'shopping-cart'
      });
      
      budgetStrategies.push({
        category: 'Utilities & Energy',
        monthlySavings: energySavings,
        actions: [
          'Lower thermostat 1-2 degrees in winter',
          'Use LED bulbs and smart power strips',
          'Run appliances during off-peak hours',
          'Compare rates from different providers'
        ],
        icon: 'zap'
      });
    }
    
    // Negotiation scripts
    const negotiationScripts = {
      currentLender: `"Hi, I'm calling about my mortgage renewal coming up on ${renewalDate.toLocaleDateString('en-CA')}. I've been a loyal customer for [X years] and I'm getting competitive offers from other lenders at ${(renewalRate - 0.25).toFixed(2)}%. Can you match or beat this rate? I'd prefer to stay with you, but I need to do what's best for my family financially."`,
      
      newLender: `"Hi, I'm shopping for a mortgage renewal. My current balance is $${mortgage.current_balance?.toLocaleString()}, and I'm looking for the best 5-year fixed rate. I have excellent credit and have never missed a payment. What's your best rate for a customer like me?"`,
      
      followUp: `"Thank you for the offer of ${renewalRate}%. I've received a lower rate from [Competitor Name]. Is there any room to improve on this? I'm ready to commit today if you can get closer to [target rate]%."`
    };
    
    // Rate-specific strategies
    const rateStrategies = [];
    
    if (renewalRate > currentRate + 1) {
      rateStrategies.push({
        strategy: 'Consider Variable Rate',
        description: 'Variable rates are currently lower and may save you money if rates stabilize or drop.',
        potentialSavings: Math.round(monthlyIncrease * 0.3),
        risk: 'medium',
        suitableFor: 'Those comfortable with rate fluctuations'
      });
    }
    
    rateStrategies.push({
      strategy: 'Extend Amortization',
      description: 'Extending from 20 to 25 years can reduce monthly payments significantly.',
      potentialSavings: Math.round(monthlyIncrease * 0.2),
      risk: 'low',
      suitableFor: 'Those prioritizing cash flow over total interest'
    });
    
    rateStrategies.push({
      strategy: 'Lump Sum Payment Before Renewal',
      description: 'Applying savings to principal reduces the balance before higher rates kick in.',
      potentialSavings: Math.round(monthlyIncrease * 0.15),
      risk: 'low',
      suitableFor: 'Those with available savings or bonuses'
    });
    
    if (mortgage.home_value && mortgage.current_balance) {
      const ltv = (mortgage.current_balance / mortgage.home_value) * 100;
      if (ltv < 65) {
        rateStrategies.push({
          strategy: 'HELOC Hybrid',
          description: 'Convert portion to HELOC at prime + 0.5%, reducing overall effective rate.',
          potentialSavings: Math.round(monthlyIncrease * 0.25),
          risk: 'medium',
          suitableFor: 'Those with significant equity (you have ~' + (100 - ltv).toFixed(0) + '% equity)'
        });
      }
    }
    
    return {
      urgencyPhase,
      severity,
      daysToRenewal,
      weeksToRenewal,
      renewalDate: renewalDate.toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
      monthlyIncrease,
      annualIncrease,
      monthlyBudgetAdjustment,
      weeklyBudgetAdjustment,
      savingsNeeded,
      currentRate,
      renewalRate,
      milestones,
      budgetStrategies,
      totalPotentialSavings: budgetStrategies.reduce((sum, s) => sum + s.monthlySavings, 0),
      negotiationScripts,
      rateStrategies,
      generatedAt: new Date().toISOString()
    };
  }, [shockData, mortgage]);
  
  const copyToClipboard = async (text, section) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
      toast.success('Copied to clipboard');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };
  
  const generatePrintablePlan = () => {
    if (!actionPlan) return;
    
    trackFeatureUsed('action_plan_print');
    
    const printContent = `
PERSONALIZED MORTGAGE RENEWAL ACTION PLAN
Generated: ${new Date().toLocaleDateString('en-CA')}
==========================================

RENEWAL OVERVIEW
----------------
Renewal Date: ${actionPlan.renewalDate}
Days Until Renewal: ${actionPlan.daysToRenewal}
Current Rate: ${actionPlan.currentRate}%
Expected New Rate: ${actionPlan.renewalRate}%

PAYMENT IMPACT
--------------
Monthly Increase: $${actionPlan.monthlyIncrease.toFixed(2)}
Annual Impact: $${actionPlan.annualIncrease.toFixed(2)}
Budget Adjustment Needed: $${actionPlan.monthlyBudgetAdjustment}/month

ACTION TIMELINE
---------------
${actionPlan.milestones.map(m => `
${m.phase} Phase - Due: ${m.deadline}
${m.tasks.map(t => `  • ${t}`).join('\n')}
`).join('\n')}

BUDGET ADJUSTMENT STRATEGIES
----------------------------
${actionPlan.budgetStrategies.map(s => `
${s.category} - Save ~$${s.monthlySavings}/month
${s.actions.map(a => `  • ${a}`).join('\n')}
`).join('\n')}

Total Potential Monthly Savings: $${actionPlan.totalPotentialSavings}

RATE STRATEGIES TO CONSIDER
---------------------------
${actionPlan.rateStrategies.map(s => `
${s.strategy}
  ${s.description}
  Potential Savings: ~$${s.potentialSavings}/month
  Risk Level: ${s.risk}
`).join('\n')}

NEGOTIATION SCRIPTS
-------------------

For Current Lender:
${actionPlan.negotiationScripts.currentLender}

For New Lenders:
${actionPlan.negotiationScripts.newLender}

Follow-up Script:
${actionPlan.negotiationScripts.followUp}

==========================================
Generated by My Mortgage Canada
mymortgagecanada.app
    `;
    
    const blob = new Blob([printContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mortgage_action_plan_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Action plan downloaded!');
  };
  
  if (!actionPlan) {
    return null;
  }
  
  const getUrgencyColor = () => {
    switch (actionPlan.urgencyPhase) {
      case 'critical': return 'border-red-500 bg-red-500/10';
      case 'urgent': return 'border-orange-500 bg-orange-500/10';
      case 'active': return 'border-yellow-500 bg-yellow-500/10';
      default: return 'border-primary bg-primary/10';
    }
  };
  
  return (
    <div data-testid="action-plan-generator" className="space-y-4 mt-6">
      {/* Action Plan Header */}
      <div className={`border-2 rounded-xl p-6 ${getUrgencyColor()}`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-primary/20 rounded-xl flex items-center justify-center">
              <Target className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                Your Personalized Action Plan
                <span className={`text-xs px-2 py-1 rounded-full ${
                  actionPlan.urgencyPhase === 'critical' ? 'bg-red-500/20 text-red-400' :
                  actionPlan.urgencyPhase === 'urgent' ? 'bg-orange-500/20 text-orange-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {actionPlan.urgencyPhase === 'critical' ? 'Act Now!' : 
                   actionPlan.urgencyPhase === 'urgent' ? 'Urgent' : 'Active'}
                </span>
              </h3>
              <p className="text-sm text-muted-foreground">
                {actionPlan.weeksToRenewal} weeks to prepare for your renewal on {actionPlan.renewalDate}
              </p>
            </div>
          </div>
          
          <button
            onClick={generatePrintablePlan}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors"
          >
            <Download className="h-4 w-4" />
            Download Plan
          </button>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-background/50 rounded-lg p-3 text-center">
            <p className="text-xs text-muted-foreground">Budget Adjustment</p>
            <p className="text-xl font-bold text-primary">${actionPlan.monthlyBudgetAdjustment}/mo</p>
          </div>
          <div className="bg-background/50 rounded-lg p-3 text-center">
            <p className="text-xs text-muted-foreground">Potential Savings</p>
            <p className="text-xl font-bold text-green-400">${actionPlan.totalPotentialSavings}/mo</p>
          </div>
          <div className="bg-background/50 rounded-lg p-3 text-center">
            <p className="text-xs text-muted-foreground">Emergency Buffer</p>
            <p className="text-xl font-bold">${actionPlan.savingsNeeded.toLocaleString()}</p>
          </div>
          <div className="bg-background/50 rounded-lg p-3 text-center">
            <p className="text-xs text-muted-foreground">Rate Strategies</p>
            <p className="text-xl font-bold">{actionPlan.rateStrategies.length} options</p>
          </div>
        </div>
        
        <button
          onClick={() => setShowFullPlan(!showFullPlan)}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm text-primary hover:text-primary/80 transition-colors"
        >
          {showFullPlan ? (
            <>Hide Detailed Plan <ChevronUp className="h-4 w-4" /></>
          ) : (
            <>View Full Action Plan <ChevronDown className="h-4 w-4" /></>
          )}
        </button>
      </div>
      
      {/* Expanded Full Plan */}
      {showFullPlan && (
        <div className="space-y-4">
          {/* Timeline */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Action Timeline
            </h4>
            
            <div className="space-y-4">
              {actionPlan.milestones.map((milestone, idx) => (
                <div key={idx} className="relative pl-8 pb-4 border-l-2 border-border last:border-l-0 last:pb-0">
                  <div className={`absolute left-[-9px] top-0 h-4 w-4 rounded-full ${
                    milestone.priority === 'high' ? 'bg-red-500' : 
                    milestone.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`} />
                  
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h5 className="font-semibold">{milestone.phase} Phase</h5>
                      <p className="text-xs text-muted-foreground">
                        Due: {milestone.deadline} ({milestone.daysFromNow} days)
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      milestone.priority === 'high' ? 'bg-red-500/20 text-red-400' : 
                      milestone.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 
                      'bg-green-500/20 text-green-400'
                    }`}>
                      {milestone.priority}
                    </span>
                  </div>
                  
                  <ul className="space-y-1">
                    {milestone.tasks.map((task, tidx) => (
                      <li key={tidx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <span>{task}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          
          {/* Budget Strategies */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Budget Adjustment Strategies
              <span className="ml-auto text-sm font-normal text-green-400">
                Save up to ${actionPlan.totalPotentialSavings}/month
              </span>
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {actionPlan.budgetStrategies.map((strategy, idx) => (
                <div key={idx} className="bg-muted/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold">{strategy.category}</h5>
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                      -${strategy.monthlySavings}/mo
                    </span>
                  </div>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {strategy.actions.map((action, aidx) => (
                      <li key={aidx} className="flex items-start gap-2">
                        <span className="text-primary">•</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          
          {/* Rate Strategies */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-primary" />
              Rate Reduction Strategies
            </h4>
            
            <div className="space-y-3">
              {actionPlan.rateStrategies.map((strategy, idx) => (
                <div key={idx} className="bg-muted/20 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h5 className="font-semibold">{strategy.strategy}</h5>
                      <p className="text-sm text-muted-foreground">{strategy.description}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-green-400 font-bold">~${strategy.potentialSavings}/mo</span>
                      <p className={`text-xs ${
                        strategy.risk === 'low' ? 'text-green-400' :
                        strategy.risk === 'medium' ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {strategy.risk} risk
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Best for: {strategy.suitableFor}
                  </p>
                </div>
              ))}
            </div>
          </div>
          
          {/* Negotiation Scripts */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Phone className="h-5 w-5 text-primary" />
              Negotiation Scripts
              <span className="text-xs text-muted-foreground font-normal ml-2">Click to copy</span>
            </h4>
            
            <div className="space-y-4">
              <div className="bg-muted/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-semibold">For Current Lender</h5>
                  <button
                    onClick={() => copyToClipboard(actionPlan.negotiationScripts.currentLender, 'current')}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                  >
                    {copiedSection === 'current' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copiedSection === 'current' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-sm text-muted-foreground italic">
                  "{actionPlan.negotiationScripts.currentLender}"
                </p>
              </div>
              
              <div className="bg-muted/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-semibold">For New Lenders</h5>
                  <button
                    onClick={() => copyToClipboard(actionPlan.negotiationScripts.newLender, 'new')}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                  >
                    {copiedSection === 'new' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copiedSection === 'new' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-sm text-muted-foreground italic">
                  "{actionPlan.negotiationScripts.newLender}"
                </p>
              </div>
              
              <div className="bg-muted/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-semibold">Follow-up Script</h5>
                  <button
                    onClick={() => copyToClipboard(actionPlan.negotiationScripts.followUp, 'followup')}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80"
                  >
                    {copiedSection === 'followup' ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copiedSection === 'followup' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-sm text-muted-foreground italic">
                  "{actionPlan.negotiationScripts.followUp}"
                </p>
              </div>
            </div>
          </div>
          
          {/* Pro Tip */}
          <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h5 className="font-semibold mb-1">Pro Tip</h5>
                <p className="text-sm text-muted-foreground">
                  Start your budget adjustments {actionPlan.weeksToRenewal > 8 ? '8 weeks' : 'immediately'} before renewal. 
                  This builds your emergency buffer and helps you adjust to the new payment amount gradually. 
                  Set up a separate savings account and transfer ${actionPlan.weeklyBudgetAdjustment}/week to simulate the new payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionPlanGenerator;
